<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import Input from 'dashboard/components-next/input/Input.vue';
import BotPhasesEditor from './BotPhasesEditor.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  bot: { type: Object, required: true },
  isUpdating: { type: Boolean, default: false },
});

const emit = defineEmits(['save']);

const { t } = useI18n();

// The numbers the runtime reads straight off the bot. Grouped the way somebody tunes them: the
// wait before answering, then how the answer comes out.
const DEBOUNCE_FIELDS = [
  'debounce_seconds',
  'debounce_max_wait',
  'debounce_max_messages',
];
const CHUNKING_FIELDS = [
  'chunking_max_chars',
  'chunking_min_chars',
  'chunking_max_messages',
  'chunking_typing_cps',
  'chunking_min_delay',
  'chunking_max_delay',
];
// Rules with a closed set of values. labels_any, labels_none and contact_filter take free values
// and are shown as they are until they have an editor of their own.
const FREE_FORM_RULES = ['labels_any', 'labels_none', 'contact_filter'];

const phases = ref([]);

const form = reactive({
  debounce_seconds: 0,
  debounce_max_wait: 0,
  debounce_max_messages: 0,
  chunking_max_chars: 0,
  chunking_min_chars: 0,
  chunking_max_messages: 0,
  chunking_typing_cps: 0,
  chunking_min_delay: 0,
  chunking_max_delay: 0,
  chunking_on_interrupt: 'cancel_remaining',
  max_actions_per_turn: 0,
  statuses: '',
  assignment: '',
  business_hours: '',
  message_types: '',
});

const fillFromBot = bot => {
  [...DEBOUNCE_FIELDS, ...CHUNKING_FIELDS, 'max_actions_per_turn'].forEach(
    field => {
      form[field] = bot[field] ?? 0;
    }
  );
  form.chunking_on_interrupt = bot.chunking_on_interrupt || 'cancel_remaining';

  // A phase declared as a plain name is the same phase with no policy, so the editor works on one
  // shape only.
  phases.value = (bot.bot_config?.phases || []).map(phase =>
    typeof phase === 'string'
      ? { name: phase, tools: [], agent_bot_id: '' }
      : {
          name: phase.name,
          tools: phase.tools || [],
          agent_bot_id: phase.agent_bot_id || '',
        }
  );

  const rules = bot.engagement_rules || {};
  form.statuses = (rules.statuses || []).join(',');
  form.assignment = rules.assignment || '';
  form.business_hours = rules.business_hours || '';
  form.message_types = (rules.message_types || []).join(',');
};

watch(() => props.bot, fillFromBot, { immediate: true, deep: true });

const interruptOptions = computed(() => [
  {
    value: 'cancel_remaining',
    label: t('AGENT_BOTS.BEHAVIOR.INTERRUPT.CANCEL'),
  },
  { value: 'send_all', label: t('AGENT_BOTS.BEHAVIOR.INTERRUPT.SEND_ALL') },
]);

const anyOption = computed(() => ({
  value: '',
  label: t('AGENT_BOTS.BEHAVIOR.RULES.ANY'),
}));

const statusOptions = computed(() => [
  anyOption.value,
  { value: 'open', label: t('AGENT_BOTS.BEHAVIOR.RULES.STATUS_OPEN') },
  {
    value: 'open,pending',
    label: t('AGENT_BOTS.BEHAVIOR.RULES.STATUS_OPEN_PENDING'),
  },
  { value: 'pending', label: t('AGENT_BOTS.BEHAVIOR.RULES.STATUS_PENDING') },
]);

const assignmentOptions = computed(() => [
  anyOption.value,
  { value: 'unassigned', label: t('AGENT_BOTS.BEHAVIOR.RULES.UNASSIGNED') },
  { value: 'bot_assigned', label: t('AGENT_BOTS.BEHAVIOR.RULES.BOT_ASSIGNED') },
]);

const businessHoursOptions = computed(() => [
  anyOption.value,
  { value: 'inside', label: t('AGENT_BOTS.BEHAVIOR.RULES.INSIDE_HOURS') },
  { value: 'outside', label: t('AGENT_BOTS.BEHAVIOR.RULES.OUTSIDE_HOURS') },
]);

const messageTypeOptions = computed(() => [
  anyOption.value,
  { value: 'incoming', label: t('AGENT_BOTS.BEHAVIOR.RULES.INCOMING') },
  {
    value: 'incoming,outgoing',
    label: t('AGENT_BOTS.BEHAVIOR.RULES.BOTH_WAYS'),
  },
]);

const debounceFields = computed(() =>
  DEBOUNCE_FIELDS.map(field => ({
    field,
    label: t(`AGENT_BOTS.BEHAVIOR.FIELDS.${field.toUpperCase()}`),
  }))
);

const chunkingFields = computed(() =>
  CHUNKING_FIELDS.map(field => ({
    field,
    label: t(`AGENT_BOTS.BEHAVIOR.FIELDS.${field.toUpperCase()}`),
  }))
);

// What an operator set through the API and this screen cannot edit yet. Showing it beats a screen
// that silently drops it on the next save.
const freeFormRules = computed(() => {
  const rules = props.bot.engagement_rules || {};
  return FREE_FORM_RULES.filter(key => {
    const value = rules[key];
    return Array.isArray(value) ? value.length : Boolean(value);
  }).map(key => ({
    key,
    label: t(`AGENT_BOTS.BEHAVIOR.FIELDS.${key.toUpperCase()}`),
    value: Array.isArray(rules[key])
      ? rules[key].join(', ')
      : Object.entries(rules[key])
          .map(([name, item]) => `${name}: ${item}`)
          .join(', '),
  }));
});

const toList = value =>
  value
    .split(',')
    .map(item => item.trim())
    .filter(Boolean);

const handleSave = () => {
  const rules = { ...(props.bot.engagement_rules || {}) };

  if (form.statuses) rules.statuses = toList(form.statuses);
  else delete rules.statuses;
  if (form.assignment) rules.assignment = form.assignment;
  else delete rules.assignment;
  if (form.business_hours) rules.business_hours = form.business_hours;
  else delete rules.business_hours;
  if (form.message_types) rules.message_types = toList(form.message_types);
  else delete rules.message_types;

  // Phases ride with the rest of the bot's settings: nobody tunes a funnel and saves it separately
  // from the behaviour it governs.
  const declaredPhases = phases.value
    .filter(phase => phase.name.trim())
    .map(phase => {
      const declared = { name: phase.name.trim() };
      if (phase.tools?.length) declared.tools = phase.tools;
      if (phase.agent_bot_id)
        declared.agent_bot_id = Number(phase.agent_bot_id);
      return declared;
    });

  const payload = {
    engagement_rules: rules,
    chunking_on_interrupt: form.chunking_on_interrupt,
    bot_config: { ...(props.bot.bot_config || {}), phases: declaredPhases },
  };
  [...DEBOUNCE_FIELDS, ...CHUNKING_FIELDS, 'max_actions_per_turn'].forEach(
    field => {
      payload[field] = Number(form[field]);
    }
  );

  emit('save', payload);
};
</script>

<template>
  <div class="flex flex-col gap-6">
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div
        class="flex flex-col gap-4 p-5 rounded-xl bg-n-solid-1 outline outline-1 outline-n-weak"
      >
        <div class="flex flex-col gap-1">
          <h3 class="text-base font-medium text-n-slate-12">
            {{ $t('AGENT_BOTS.BEHAVIOR.DEBOUNCE.TITLE') }}
          </h3>
          <p class="text-sm text-n-slate-11">
            {{ $t('AGENT_BOTS.BEHAVIOR.DEBOUNCE.DESCRIPTION') }}
          </p>
        </div>
        <div
          v-for="item in debounceFields"
          :key="item.field"
          class="flex items-center gap-4"
        >
          <div class="flex flex-col flex-grow min-w-0">
            <span class="text-sm text-n-slate-12">{{ item.label }}</span>
            <span class="text-xs text-n-slate-10">{{ item.field }}</span>
          </div>
          <Input
            :id="item.field"
            v-model="form[item.field]"
            type="number"
            size="sm"
            custom-input-class="w-24 text-right"
          />
        </div>
      </div>

      <div
        class="flex flex-col gap-4 p-5 rounded-xl bg-n-solid-1 outline outline-1 outline-n-weak"
      >
        <div class="flex flex-col gap-1">
          <h3 class="text-base font-medium text-n-slate-12">
            {{ $t('AGENT_BOTS.BEHAVIOR.CHUNKING.TITLE') }}
          </h3>
          <p class="text-sm text-n-slate-11">
            {{ $t('AGENT_BOTS.BEHAVIOR.CHUNKING.DESCRIPTION') }}
          </p>
        </div>
        <div
          v-for="item in chunkingFields"
          :key="item.field"
          class="flex items-center gap-4"
        >
          <div class="flex flex-col flex-grow min-w-0">
            <span class="text-sm text-n-slate-12">{{ item.label }}</span>
            <span class="text-xs text-n-slate-10">{{ item.field }}</span>
          </div>
          <Input
            :id="item.field"
            v-model="form[item.field]"
            type="number"
            size="sm"
            custom-input-class="w-24 text-right"
          />
        </div>
        <div class="flex items-center gap-4">
          <span class="text-sm text-n-slate-12 flex-grow">
            {{ $t('AGENT_BOTS.BEHAVIOR.INTERRUPT.LABEL') }}
          </span>
          <div class="w-52">
            <ComboBox
              v-model="form.chunking_on_interrupt"
              :options="interruptOptions"
            />
          </div>
        </div>
      </div>
    </div>

    <div
      class="flex flex-col gap-4 p-5 rounded-xl bg-n-solid-1 outline outline-1 outline-n-weak"
    >
      <div class="flex flex-col gap-1">
        <h3 class="text-base font-medium text-n-slate-12">
          {{ $t('AGENT_BOTS.BEHAVIOR.RULES.TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11">
          {{ $t('AGENT_BOTS.BEHAVIOR.RULES.DESCRIPTION') }}
        </p>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div class="flex items-center gap-4">
          <div class="flex flex-col flex-grow min-w-0">
            <span class="text-sm text-n-slate-12">
              {{ $t('AGENT_BOTS.BEHAVIOR.FIELDS.STATUSES') }}
            </span>
            <span class="text-xs text-n-slate-10">{{ 'statuses' }}</span>
          </div>
          <div class="w-52">
            <ComboBox v-model="form.statuses" :options="statusOptions" />
          </div>
        </div>

        <div class="flex items-center gap-4">
          <div class="flex flex-col flex-grow min-w-0">
            <span class="text-sm text-n-slate-12">
              {{ $t('AGENT_BOTS.BEHAVIOR.FIELDS.ASSIGNMENT') }}
            </span>
            <span class="text-xs text-n-slate-10">{{ 'assignment' }}</span>
          </div>
          <div class="w-52">
            <ComboBox v-model="form.assignment" :options="assignmentOptions" />
          </div>
        </div>

        <div class="flex items-center gap-4">
          <div class="flex flex-col flex-grow min-w-0">
            <span class="text-sm text-n-slate-12">
              {{ $t('AGENT_BOTS.BEHAVIOR.FIELDS.BUSINESS_HOURS') }}
            </span>
            <span class="text-xs text-n-slate-10">{{ 'business_hours' }}</span>
          </div>
          <div class="w-52">
            <ComboBox
              v-model="form.business_hours"
              :options="businessHoursOptions"
            />
          </div>
        </div>

        <div class="flex items-center gap-4">
          <div class="flex flex-col flex-grow min-w-0">
            <span class="text-sm text-n-slate-12">
              {{ $t('AGENT_BOTS.BEHAVIOR.FIELDS.MESSAGE_TYPES') }}
            </span>
            <span class="text-xs text-n-slate-10">{{ 'message_types' }}</span>
          </div>
          <div class="w-52">
            <ComboBox
              v-model="form.message_types"
              :options="messageTypeOptions"
            />
          </div>
        </div>
      </div>

      <div
        v-if="freeFormRules.length"
        class="flex flex-col gap-2 p-3 rounded-lg bg-n-alpha-2"
      >
        <span class="text-xs text-n-slate-11">
          {{ $t('AGENT_BOTS.BEHAVIOR.RULES.API_ONLY') }}
        </span>
        <div
          v-for="rule in freeFormRules"
          :key="rule.key"
          class="flex items-center gap-3 text-sm"
        >
          <span class="text-n-slate-11 w-44">{{ rule.label }}</span>
          <span class="text-n-slate-12">{{ rule.value }}</span>
        </div>
      </div>

      <div class="flex items-center gap-4 pt-2 border-t border-n-weak">
        <div class="flex flex-col flex-grow min-w-0">
          <span class="text-sm text-n-slate-12">
            {{ $t('AGENT_BOTS.BEHAVIOR.FIELDS.MAX_ACTIONS_PER_TURN') }}
          </span>
          <span class="text-xs text-n-slate-10">
            {{ 'max_actions_per_turn ·' }}
            {{ $t('AGENT_BOTS.BEHAVIOR.MAX_ACTIONS_HINT') }}
          </span>
        </div>
        <Input
          id="max_actions_per_turn"
          v-model="form.max_actions_per_turn"
          type="number"
          size="sm"
          custom-input-class="w-24 text-right"
        />
      </div>
    </div>

    <BotPhasesEditor v-model="phases" :bot="bot" />

    <div class="flex justify-end">
      <Button
        :label="$t('AGENT_BOTS.BEHAVIOR.SAVE')"
        size="sm"
        :is-loading="isUpdating"
        @click="handleSave"
      />
    </div>
  </div>
</template>
